import { Organization, User } from '@isocel/front-common';

export interface BaseModel {
    id: string;
    name: string;
    _id?: string;
    organization?: string | Organization;
    SelectionType?: string;
    image?: string;
    iconRemoteFile?: RemoteFile;
    store?: string | Store;
}

export interface Store extends BaseModel {
    code: string;
    organization: string | Organization;
    contact?: string;
    name: string;
    ean?: string;
    receptionAutoActiver?: boolean;
    receptionAutoDelai?: number | null;
    aliceCode?: string;
}

export interface Supplier extends BaseModel {
    store: string | Store;
}
export enum LoadingSessionStatus {
    New = 0,
    Completed = 1,
}

export enum PackagingFlowDirection {
    ShippedToStore = 0,
    RecivedInStore = 1,
    ShippedToOrganization = 2,
    RecivedInOrganization = 3,
    ReceivedFromSupplier = 4,
    Purchase = 5,
    Sale = 6,
    Destruction = 7,
    ShipmentForRepair = 8,
    ReceptionFromRepair = 9,
    ReturnToSupplier = 10,
    Transfer = 11,
    ReceivedFromSupplierAtOrganization = 12,
    DebtReduction = 13,
    Increase = 14,
    Decrease = 15,
    AutomaticReception = 16,
    ReturnToProvider = 17,
    ReceiptFromStoreExchange = 18,
}

export enum DocumentTypes {
    LettreDeVoiture = 'Lettre de voiture',
    PhotosEmballages = 'Photos emballages',
    Autres = 'Autres',
}

export enum PackagingVoucherStatus {
    Expired = 'Expiré',
    NotUsed = 'Non Utilisé',
    Used = 'Utilisé',
}

export interface LoadingSession {
    id: any;
    warehouse: BaseModel;
    store: BaseModel;
    transporter: BaseModel;
    trailerNumber: string;
    status: LoadingSessionStatus;
    createdAt: Date;
    date: Date;
    time: Date;
}

export interface OrgInternCode {
    _id?: string;
    organization?: string | Organization;
    code: string;
    ean?: number[];
    unitPriceToInvoice?: number;
    tvaCodeToInvoice?: string;
}

export interface Package {
    id: string;
    type: BaseModel;
    category: BaseModel;
    model: BaseModel;
    organization?: string | Organization;
    rental?: boolean;
    isInvoiceable?: boolean;
    iconRemoteFile?: RemoteFile;
    ean?: number[]; // Array of EAN codes for organization inter-codes
    orgInternCodes?: OrgInternCode[];
}

export interface PackagingConfig {
    packagingTypes: BaseModel[];
    packageCategories: BaseModel[];
    packages: Package[];
}

export interface PackagingFlowItem {
    package: string | Package;
    shippedQuantity: number;
    goodCount: number;
    badCount: number;
    organizationRefused?: boolean | null;
    toBeReturned?: number;
    statusDifferenceReported?: string;
}

export interface PackagingFlow {
    id?: string;
    _id?: string;
    createdAt?: string;
    sender: string | BaseModel | any;
    recipient: string | BaseModel | any;
    user: string | BaseModel;
    loadingSession?: string | LoadingSession;
    transportVoucher?: string;
    flowType: PackagingFlowDirection;
    packages: PackagingFlowItem[];
    type?: BaseModel[];
    comment?: string;
    originId?: string | null;
    received?: boolean;
    cmr?: string;
    organizationId?: string;
    packagingVouchers?: PackagingVoucher[];
    senderType?: string | BaseModel | any;
    recieverType?: string | BaseModel | any;
    attachments?: DocumentAttachment[];
    totalQuantity?: number;
    differenceReported?: boolean;
    transporter?: string;
    expeditionAt?: string;
    deliveredAt?: string;
    truckNumber?: string;
    orderNumber?: string;
    packagingVoucherUsed?: PackagingVoucher[];
    invoiceId?: string | null;
    invoiceName?: string | null;
}

export interface DocumentAttachment {
    attachmentType: DocumentTypes;
    remoteFile?: RemoteFile;
}
export interface RemoteFile {
    originalName?: string;
    filename: string;
    mimeType: string;
    bucket: string;
    createdAt?: string;
    updatedAt?: string;
    id?: string;
    organization?: string; // Organization ID linked to the file
    // Note: Use signed URLs for accessing files from private buckets
    // Call GET /files/:fileId/signed-url to get a temporary public URL
}

export interface OrganizationOption {
    value: string;
    id: string;
}

export interface TransporterOptions {
    transporter: string;
    value: string;
}

export interface SupplierOptions {
    supplier: string;
    value: string;
}
export interface PaginateResult<T> {
    items: T[];
    page: number;
    pageCount: number;
    pageSize: number;
    totalCount: number;
}
export interface GetAllPackagingFlowParams {
    page?: number;
    pageSize?: number;
    differenceReported?: boolean;
    packageIds?: string[];
    status?: string[];
    voucherStatus?: string[];
    storeIds?: string | string[];
    supplierIds?: string[];
    parcPalettIds?: string[];
    warehouseIds?: string[];
    transportVoucher?: string;
    bonDeTransport?: string[];
    invoiceName?: string;
    flowType?: PackagingFlowDirection;
    sortField?: string;
    sortOrder?: string;
    isMounted?: boolean;
    startDate?: string | null;
    endDate?: string | null;
    organization?: string;
    litigeStatus?: string[];
    received?: boolean;
}

export interface PackagingVoucher {
    createdAt: string;
    flowReceptionId: string;
    flowReturningId: string;
    organization: string;
    voucherNumber: string;
    _id: string;
}
export interface VoucherResponse {
    message: string;
    success: boolean;
    voucher: PackagingVoucher;
}

export enum Permission {
    superAdmin = 'superAdmin',
    orgAdmin = 'orgAdmin',
    orgUser = 'orgUser',
    storeAdmin = 'storeAdmin',
    storeUser = 'storeUser',
    users = 'users',
    organizations = 'organizations',
    logAs = 'logAs',
    dashboard = 'dashboard',
    roles = 'roles',
    staticPages = 'staticPages',
}

export enum RoleSlug {
    superAdmin = 'superAdmin',
    orgAdmin = 'orgAdmin',
    orgUser = 'orgUser',
    storeAdmin = 'storeAdmin',
    storeUser = 'storeUser',
}

export interface AppUser extends User {
    needPasswordChange?: boolean;
}

export interface StoreUser extends AppUser {
    store?: string;
    storeDetails?: Store;
}

export interface MiscFlowType {
    name: string;
    label: string;
}

export interface MiscFlowTypesResponse {
    hasAccess: boolean;
    flowTypes: MiscFlowType[];
}
export enum InventoryStatus {
    notVerified = 'Non vérifié',
    accepted = 'Accepté',
    refused = 'Refusé',
}

export interface PackageInventoryItem {
    package: string;
    countedValue: number;
    actualDifference: number;
    status?: InventoryStatus;
    // Front-only: stamped when grouping inventories
    inventoryDate?: string;
    inventoryId?: string;
}

export interface StoreInventory {
    _id: string;
    createdAt: string;
    organizationId: string;
    storeId: string;
    storeUserId: string;
    storeUserName?: string;
    storeName?: string;
    packagesInventory: PackageInventoryItem[];
}
export interface Invoice {
    _id: string;
    createdAt: string;
    startDate: string;
    endDate: string;
    userId: string;
    name: string;
    organizationId?: string;
    /** Display name of the user who created the invoice, populated by the API. */
    userName?: string;
    /** Free-form status returned by the backend (e.g. "sent" / "pending" / "cancelled"). */
    status?: string;
}

export interface InvoiceDetails {
    _id: string;
    invoiceId: string;
    storeId: string;
    packagesId: string;
    value: number;
}
export interface InvoicePreviewRow {
    storeId: string;
    storeCode: string;
    storeName: string;
    packageId: string;
    packageEan: string;
    packageInternCode: string; // GAEL code
    packageTypeName: string;
    packageModelName: string;
    quantity: number;
    // Optional in / out breakdown returned by the backend for debugging / display.
    inQuantity?: number;
    outQuantity?: number;
    unitPriceToInvoice?: number;
    tvaCodeToInvoice?: string;
}

export interface InvoicePreviewResponse {
    rows: InvoicePreviewRow[];
    flowIds: string[];
}

export interface CreateInvoicePayload {
    organizationId: string;
    startDate: string; // ISO
    endDate: string; // ISO
    storeIds: string[];
    name?: string;
}

export interface CreateInvoiceResponse {
    invoice: Invoice;
    details: InvoiceDetails[];
    linkedFlowsCount: number;
}

export interface InvoiceDetailsRow {
    _id: string;
    storeId: string;
    storeCode: string;
    storeName: string;
    packageId: string;
    packageEan: string;
    packageInternCode: string; // GAEL code
    packageTypeName: string;
    packageModelName: string;
    value: number;
    unitPrice?: number;
    tvaCode?: string;
}

export interface InvoiceDetailsResponse {
    invoice: Invoice;
    details: InvoiceDetailsRow[];
}

export interface GetInvoicesParams {
    organizationId: string;
    storeIds?: string[];
    startDate?: string | null;
    endDate?: string | null;
}
